import {
  alpha,
  createTheme,
  darken,
  lighten,
  ThemeProvider,
  type Shadows,
} from '@mui/material'
import { ConfirmProvider } from 'material-ui-confirm'
import type { WorkspaceDomainClaim } from 'types/graphql'

import { FatalErrorBoundary, RedwoodProvider } from '@redwoodjs/web'
import { RedwoodApolloProvider } from '@redwoodjs/web/apollo'

import type {} from '@mui/x-data-grid-premium/themeAugmentation'

import { AuthProvider, useAuth } from 'src/auth'
import FatalErrorPage from 'src/pages/FatalErrorPage'
import Routes from 'src/Routes'

import './scaffold.css'
import './index.css'
import { shadowColors } from './lib/style'

const lightShadowDefinition = [
  'none',
  `
    0 1px 2px rgba(136, 161, 183, 0.1),
    0 2px 4px rgba(136, 161, 183, 0.1)
  `,
  `
    0 1px 2px rgba(136, 161, 183, 0.07),
    0 2px 4px rgba(136, 161, 183, 0.07),
    0 4px 8px rgba(136, 161, 183, 0.07),
    0 8px 16px rgba(136, 161, 183, 0.07),
    0 16px 32px rgba(136, 161, 183, 0.07),
    0 32px 64px rgba(136, 161, 183, 0.07)
  `,
  `
    0 1px 2px rgba(136, 161, 183, 0.1),
    0 2px 4px rgba(136, 161, 183, 0.1),
    0 4px 8px rgba(136, 161, 183, 0.1),
    0 8px 16px rgba(136, 161, 183, 0.1)
  `,
]

const shadows = [
  ...lightShadowDefinition,
  ...Array(25 - lightShadowDefinition.length).fill(`
      0 1px 2px rgba(136, 161, 183, 0.07),
      0 2px 4px rgba(136, 161, 183, 0.07)
    `),
] as Shadows

declare module '@mui/material/styles' {
  interface Palette {
    columns: Palette['primary']
    filters: Palette['primary']
    groupings: Palette['primary']
    sorts: Palette['primary']
    pinned: Palette['primary']
    top: Palette['primary']
    middle: Palette['primary']
    bottom: Palette['primary']
    shadow: Palette['primary']
  }

  interface PaletteOptions {
    columns?: PaletteOptions['primary']
    filters?: PaletteOptions['primary']
    groupings?: PaletteOptions['primary']
    sorts?: PaletteOptions['primary']
    pinned?: PaletteOptions['primary']
    top?: PaletteOptions['primary']
    middle?: PaletteOptions['primary']
    bottom?: PaletteOptions['primary']
    shadow?: PaletteOptions['primary']
  }
}

const DayAiDefaultTheme = createTheme({
  typography: {
    fontFamily: ['Poppins'].join(','),
    fontSize: 12,
    h1: {
      fontWeight: 600,
      fontSize: '30px',
      lineHeight: '130%',
      letterSpacing: '-1.5px',
    },
    h2: {
      fontWeight: 600,
      fontSize: '22px',
      letterSpacing: '-1.6px',
    },
    h3: {
      fontWeight: 600,
      fontSize: '18px',
      mb: 2,
      letterSpacing: '-0.9px',
    },
    h4: {
      fontWeight: 600,
      fontSize: '15px',
      letterSpacing: '-0.5px',
    },
    h5: {
      fontSize: '13px',
      lineHeight: '130%',
      fontWeight: 600,
      letterSpacing: '-0.22px',
    },
    h6: {
      fontSize: '13px',
      lineHeight: '130%',
      fontWeight: 600,
      letterSpacing: '-0.22px',
    },
    body1: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '140%',
      letterSpacing: '-0.5px',
      fontFamily: 'DM Sans',
    },
    body2: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '130%',
      letterSpacing: '-0.5px',
      fontFamily: 'DM Sans',
    },
    subtitle2: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '130%',
      letterSpacing: '-0.5px',
      fontFamily: 'DM Sans',
    },
  },
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: '#F8F9FC', // '#F9FAFB',
          paper: '#FFFFFF',
        },
        top: {
          main: '#FFFFFF',
          light: lighten('#FFFFFF', 0.2),
          dark: darken('#FFFFFF', 0.2),
          contrastText: '#37352f',
        },
        middle: {
          main: alpha('#F8F9FC', 0.5),
          light: alpha('#F8F9FC', 0.5),
          dark: alpha('#F8F9FC', 0.5),
          contrastText: '#37352f',
        },
        bottom: {
          main: '#FFFFFF',
          light: lighten('#FFFFFF', 0.2),
          dark: darken('#FFFFFF', 0.2),
          contrastText: '#37352f',
        },
        shadow: {
          main: shadowColors.light,
          light: lighten(shadowColors.light, 0.2),
          dark: darken(shadowColors.light, 0.2),
          contrastText: '#37352f',
        },
        primary: {
          main: '#37352f',
          contrastText: lighten('#FEEFD9', 0.9),
        },
        secondary: {
          main: '#3F7EB4', // '#007aff',
          light: 'rgba(0, 145, 228, 0.2)',
          contrastText: '#FFFFFF',
        },
        info: {
          main: '#4F7FAF',
          contrastText: '#FFFFFF',
        },
        warning: {
          main: '#EEB057',
          contrastText: '#FFFFFF',
        },
        error: {
          main: '#CA3A52',
          contrastText: '#FFFFFF',
        },
        success: {
          main: '#5FC18F',
          contrastText: '#FFFFFF',
        },
        text: {
          primary: '#37352f', //'#231F20', //'#101925',
          secondary: '#354052',
          disabled: lighten('#354052', 0.7),
        },
        action: {
          selected: darken('#F5F7FA', 0.03),
        },
        columns: {
          main: '#8A6FD1',
          light: alpha('#8A6FD1', 0.14),
          dark: alpha('#8A6FD1', 0.3),
          contrastText: '#8A6FD1',
        },
        filters: {
          main: '#37B393',
          light: alpha('#37B393', 0.14),
          dark: alpha('#37B393', 0.3),
          contrastText: '#37B393',
        },
        groupings: {
          main: '#DE7C45',
          light: alpha('#DE7C45', 0.14),
          dark: alpha('#DE7C45', 0.3),
          contrastText: '#DE7C45',
        },
        sorts: {
          main: '#4B97D2',
          light: alpha('#4B97D2', 0.14),
          dark: alpha('#4B97D2', 0.3),
          contrastText: '#4B97D2',
        },
        pinned: {
          main: '#D44B9C',
          light: alpha('#D44B9C', 0.14),
          dark: alpha('#D44B9C', 0.3),
          contrastText: '#D44B9C',
        },
      },
    },
    dark: {
      palette: {
        divider: darken('#27282D', 0.05),
        background: {
          default: lighten('#090b0d', 0.015),
          paper: darken('#14161b', 0.05),
        },
        top: {
          main: lighten('#14161b', 0.015),
          light: lighten('#14161b', 0.2),
          dark: darken('#14161b', 0.2),
          contrastText: '#110F18',
        },
        middle: {
          main: darken('#14161b', 0.05),
          light: lighten('#14161b', 0.2),
          dark: darken('#14161b', 0.2),
          contrastText: '#110F18',
        },
        bottom: {
          main: '#000000',
          light: lighten('#000000', 0.2),
          dark: darken('#000000', 0.2),
          contrastText: '#dddfe5',
        },
        shadow: {
          main: shadowColors.dark,
          light: lighten(shadowColors.dark, 0.2),
          dark: darken(shadowColors.dark, 0.2),
          contrastText: '#dddfe5',
        },
        primary: {
          main: '#FFFFFF',
          contrastText: '#110F18',
        },
        secondary: {
          main: '#3F7EB4',
        },
        info: {
          main: '#4F7FAF',
          contrastText: '#FFFFFF',
        },
        warning: {
          main: '#EEB057',
          contrastText: '#FFFFFF',
        },
        error: {
          main: '#CA3A52',
          contrastText: '#FFFFFF',
        },
        success: {
          main: '#5FC18F',
          contrastText: '#FFFFFF',
        },
        text: {
          primary: '#dddfe5', //'#231F20', //'#101925',
          secondary: darken('#dddfe5', 0.18),
          disabled: darken('#dddfe5', 0.4),
        },
        action: {
          selected: darken('#101011', 0.03),
        },
        columns: {
          main: darken('#8A6FD1', 0.4),
          light: alpha('#8A6FD1', 0.2),
          dark: darken('#8A6FD1', 0.4),
          contrastText: lighten('#8A6FD1', 0.3),
        },
        filters: {
          main: darken('#37B393', 0.4),
          light: alpha('#37B393', 0.2),
          dark: darken('#37B393', 0.4),
          contrastText: lighten('#37B393', 0.3),
        },
        groupings: {
          main: darken('#DE7C45', 0.4),
          light: alpha('#DE7C45', 0.2),
          dark: darken('#DE7C45', 0.4),
          contrastText: lighten('#DE7C45', 0.3),
        },
        sorts: {
          main: darken('#4B97D2', 0.4),
          light: alpha('#4B97D2', 0.2),
          dark: darken('#4B97D2', 0.4),
          contrastText: lighten('#4B97D2', 0.3),
        },
        pinned: {
          main: darken('#D9B434', 0.4),
          light: alpha('#D9B434', 0.2),
          dark: darken('#D9B434', 0.4),
          contrastText: lighten('#D9B434', 0.3),
        },
      },
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {},
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 6,
          '&.MuiButton-sizeMedium': {
            borderRadius: 4,
          },
          '& .MuiButton-iconSizeSmall': {
            marginRight: '4px',
          },
        }),

        outlined: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 6,
        }),

        sizeSmall: {
          fontSize: '11px',
          borderRadius: '3px',
          height: '30px',

          '& .MuiButton-startIcon': {
            marginRight: '8px',
            height: '12px',
            width: '12px',
            marginTop: '-2px',
          },
        },
        contained: ({ theme }) => ({
          boxShadow: `
            0 1px 2px ${theme.palette.shadow.main},
            0 2px 4px ${theme.palette.shadow.main},
            0 4px 8px ${theme.palette.shadow.main},
            0 8px 16px ${theme.palette.shadow.main}
          `,
        }),
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          opacity: 0.5,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          overflow: 'visible',
          minHeight: '30px',
          flexShrink: 0,
        },
        indicator: {
          height: 0,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '4px',
          flexGrow: 1,
          textTransform: 'none',
          minHeight: 0,
          minWidth: '0px',
          height: '30px',
          fontFeatureSettings: 'liga off, clig off',
          marginRight: '4px',
          transition: 'all 0.2s ease-in-out',
          opacity: 0.8,

          '& .MuiTypography-root': {
            fontSize: '11px',
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          letterSpacing: '-0.6px',
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          '& .MuiChip-label': {
            fontFamily: 'Poppins',
            fontWeight: 500,
            fontSize: '12px',
            letterSpacing: '-0.22px',
            lineHeight: '120%',
            overflowY: 'hidden',
            textOverflow: 'ellipsis',
            overflowX: 'hidden',
            whiteSpace: 'nowrap',
          },
          '& .MuiChip-icon': {
            height: '14px',
            width: '14px',
          },
          height: '24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '24px',
          borderRadius: '8px',
          boxShadow: `
            0 1px 2px ${theme.palette.shadow.main},
            0 2px 4px ${theme.palette.shadow.main},
            0 4px 8px ${theme.palette.shadow.main},
            0 8px 16px ${theme.palette.shadow.main}
          `,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '4px',
          borderRadius: '3px',
          color: theme.palette.text.primary,
          '&.loading': {
            '& .tabler-icon': {
              '@keyframes spin': {
                from: {
                  transform: 'rotate(0deg)',
                },
                to: {
                  transform: 'rotate(360deg)',
                },
              },
              animation: 'spin 1s linear infinite',
              display: 'flex',
              alignItems: 'center',
            },
          },
          boxShadow: 'none',
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: `0 0 36px 0`,
          margin: 0,
          '& .MuiTypography-h5': {
            fontSize: '18px',
            fontWeight: 600,
          },
          '& .MuiCardHeader-subheader': {
            fontSize: '14px',
            fontWeight: 400,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0 0 16px 0',
          margin: '0',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
          body {
            -webkit-font-smoothing: antialiased;
          }
        `,
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
        primary: {
          fontSize: '14px',
        },
        secondary: {
          fontSize: '11px',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '24px',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiMenuItem-root ': {
            fontSize: '12px',
          },
          borderRadius: '4px',
          '& .MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
            boxShadow: `
            0 1px 2px ${theme.palette.shadow.main},
            0 2px 4px ${theme.palette.shadow.main},
            0 4px 8px ${theme.palette.shadow.main},
            0 8px 16px ${theme.palette.shadow.main}
          `,
          },
        }),
      },
    },
  },
  shadows,
})

const App = () => {
  return (
    <FatalErrorBoundary page={FatalErrorPage}>
      <RedwoodProvider titleTemplate="%PageTitle">
        <AuthProvider>
          <RedwoodApolloProvider
            useAuth={useAuth}
            graphQLClientConfig={{
              cacheConfig: {
                typePolicies: {
                  Workspace: {
                    fields: {
                      domains: {
                        merge(
                          _existing = [],
                          incoming: WorkspaceDomainClaim[]
                        ) {
                          return incoming
                        },
                      },
                    },
                  },
                  WorkspaceMember: {
                    keyFields: ['id', 'email', 'status', 'roleId'],
                  },
                  CRMObject: {
                    keyFields: ['objectType', 'objectId'],
                  },
                  WorkAccountWorkspaceConnection: {
                    keyFields: ['workAccountUuid'],
                  },
                },
              },
            }}
          >
            <ThemeProvider theme={DayAiDefaultTheme}>
              <ConfirmProvider>
                <Routes />
              </ConfirmProvider>
            </ThemeProvider>
          </RedwoodApolloProvider>
        </AuthProvider>
      </RedwoodProvider>
    </FatalErrorBoundary>
  )
}

export default App
